import React, { useRef, useState } from 'react'
import PropTypes from 'prop-types'
import algoliasearch from 'algoliasearch'
import { isEmpty } from 'lodash'
import {
  InstantSearch,
  connectSearchBox,
  connectHits,
} from 'react-instantsearch-dom'
import Box from '@material-ui/core/Box'
import Button from '@material-ui/core/Button'
import InputAdornment from '@material-ui/core/InputAdornment'
import Popover from '@material-ui/core/Popover'
import TextField from '@material-ui/core/TextField'
import { makeStyles } from '@material-ui/core/styles'
import KeyboardArrowDownIcon from 'components/material-symbol/icons/KeyboardArrowDown'
import { ALGOLIA_APP_ID, ALGOLIA_API_KEY } from 'env'
import {
  FacilityResult,
  ResultWrapper,
  StyledPaper,
} from 'components/coaches-profile/profile-components/FacilitiesAlgolia/styles'

const useStyles = makeStyles(theme => ({
  menuPaper: {
    marginTop: theme.spacing(0.5),
    borderRadius: 12,
    boxShadow: theme.shadows[8],
  },
  popoverContent: {
    padding: theme.spacing(1.5),
    minWidth: 280,
  },
  resultsPaper: {
    margin: 0,
    marginTop: theme.spacing(1),
    boxShadow: 'none',
    maxHeight: 240,
    overflowY: 'auto',
  },
  clearButton: {
    marginTop: theme.spacing(1),
    textTransform: 'none',
  },
  selectedInput: {
    cursor: 'pointer',
  },
}))

export const formatFacilityDisplayLabel = (name, city, state) => {
  if (!name) return ''

  let label = name
  if (city) {
    label += ` - ${city}`
    if (state) {
      label += `, ${state}`
    }
  }

  return label
}

const FacilitySettingsInput = ({
  currentRefinement,
  disabled,
  error,
  helperText,
  hits,
  name,
  onSelect,
  onChange,
  onClear,
  refine,
  value,
  externalFacilityId,
  city,
  state,
  label = 'Facility Name',
  inputClassName,
}) => {
  const classes = useStyles()
  const anchorRef = useRef(null)
  const [popoverOpen, setPopoverOpen] = useState(false)

  const facilityHits = hits.filter(
    ({ object_type: objectType }) => objectType.toLowerCase() === 'facility',
  )
  const selectedLabel = externalFacilityId
    ? formatFacilityDisplayLabel(value, city, state)
    : ''
  const hasSelection = Boolean(selectedLabel)
  const showResults =
    !disabled && !isEmpty(currentRefinement) && facilityHits.length > 0

  const handleClosePopover = () => {
    setPopoverOpen(false)
    refine('')
  }

  const handleOpenPopover = () => {
    if (disabled) {
      return
    }

    refine('')
    setPopoverOpen(true)
  }

  const handleSelect = hit => {
    onSelect(hit)
    refine('')
    setPopoverOpen(false)
  }

  const handleClear = () => {
    onClear()
    refine('')
    setPopoverOpen(false)
  }

  const handleInputChange = event => {
    onChange(event)
    refine(event.currentTarget.value)
    setPopoverOpen(true)
  }

  const resultsList = facilityHits.map(hit => (
    <ResultWrapper key={hit.facility_id} onClick={() => handleSelect(hit)}>
      <FacilityResult>
        {hit.name} {hit.city && '-'} {hit.city}
        {hit.state && ','} {hit.state}
      </FacilityResult>
    </ResultWrapper>
  ))

  const searchField = (
    <TextField
      name={name}
      label={label}
      data-cy="facilityName"
      type="text"
      variant="outlined"
      className={inputClassName}
      fullWidth
      margin="none"
      value={
        hasSelection && popoverOpen
          ? currentRefinement
          : value || currentRefinement
      }
      onChange={handleInputChange}
      onFocus={handleOpenPopover}
      disabled={disabled}
      error={error}
      helperText={error ? helperText : undefined}
      autoComplete="off"
      autoFocus={hasSelection && popoverOpen}
    />
  )

  const setTextFieldAnchor = node => {
    anchorRef.current = node?.closest('.MuiOutlinedInput-root') ?? node
  }

  return (
    <>
      <TextField
        name={name}
        label={label}
        data-cy="facilityName"
        type="text"
        variant="outlined"
        className={inputClassName}
        fullWidth
        margin="none"
        value={hasSelection ? selectedLabel : value || currentRefinement}
        onChange={hasSelection ? undefined : handleInputChange}
        onClick={hasSelection && !disabled ? handleOpenPopover : undefined}
        onFocus={hasSelection ? undefined : handleOpenPopover}
        disabled={disabled}
        error={error}
        helperText={error ? helperText : undefined}
        autoComplete="off"
        inputRef={setTextFieldAnchor}
        InputProps={
          hasSelection
            ? {
              readOnly: true,
              className: disabled ? undefined : classes.selectedInput,
              endAdornment: disabled ? (
                undefined
              ) : (
                <InputAdornment position="end">
                  <KeyboardArrowDownIcon fontSize="small" />
                </InputAdornment>
              ),
            }
            : undefined
        }
        inputProps={{
          'aria-haspopup': hasSelection ? 'listbox' : undefined,
          'aria-expanded': hasSelection ? popoverOpen : undefined,
        }}
      />
      <Popover
        anchorEl={anchorRef.current}
        open={popoverOpen && (hasSelection || showResults)}
        onClose={handleClosePopover}
        disableAutoFocus
        disableEnforceFocus
        disableRestoreFocus
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        PaperProps={{
          className: classes.menuPaper,
          style: anchorRef.current
            ? { minWidth: anchorRef.current.clientWidth }
            : undefined,
        }}
      >
        {hasSelection ? (
          <Box className={classes.popoverContent}>
            {searchField}
            {onClear ? (
              <Button
                type="button"
                color="primary"
                className={classes.clearButton}
                onClick={handleClear}
              >
                Clear selection
              </Button>
            ) : null}
            {showResults ? (
              <StyledPaper className={classes.resultsPaper}>
                {resultsList}
              </StyledPaper>
            ) : null}
          </Box>
        ) : (
          <StyledPaper className={classes.resultsPaper}>
            {resultsList}
          </StyledPaper>
        )}
      </Popover>
    </>
  )
}

FacilitySettingsInput.propTypes = {
  currentRefinement: PropTypes.string,
  disabled: PropTypes.bool,
  error: PropTypes.bool,
  helperText: PropTypes.string,
  hits: PropTypes.array,
  name: PropTypes.string,
  onSelect: PropTypes.func,
  onChange: PropTypes.func,
  onClear: PropTypes.func,
  refine: PropTypes.func,
  value: PropTypes.string,
  externalFacilityId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  city: PropTypes.string,
  state: PropTypes.string,
  label: PropTypes.string,
  inputClassName: PropTypes.string,
}

const connectedHits = connectHits(FacilitySettingsInput)
const SearchBox = connectSearchBox(connectedHits)

const algoliaClient = algoliasearch(ALGOLIA_APP_ID, ALGOLIA_API_KEY)

const FacilityAlgoliaSettingsField = ({
  error,
  helperText,
  onBlur,
  ...rest
}) => (
  <InstantSearch
    searchClient={algoliaClient}
    indexName="MemberFacilityDirectory"
  >
    <SearchBox
      {...rest}
      error={error}
      helperText={helperText}
      onBlur={onBlur}
    />
  </InstantSearch>
)

FacilityAlgoliaSettingsField.propTypes = {
  onSelect: PropTypes.func,
  onClear: PropTypes.func,
  onChange: PropTypes.func,
  onBlur: PropTypes.func,
  disabled: PropTypes.bool,
  error: PropTypes.bool,
  helperText: PropTypes.string,
  value: PropTypes.string,
  name: PropTypes.string,
  externalFacilityId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  city: PropTypes.string,
  state: PropTypes.string,
  label: PropTypes.string,
  inputClassName: PropTypes.string,
}

export default FacilityAlgoliaSettingsField
